import { makeAutoObservable } from 'mobx'

import { Board } from './Board'
import { Chip } from './Chip'
import { BaseGameState } from './BaseGameState'
import { RouletteSpot, Spot, SpotColor, SpotValueType } from './Spot'
import { HighlightedCellsOnHover } from './HighlightedCellsOnHover'
import { ResultsHistoryItem } from './ResultsHistoryItem'
import { RouletteWorld3D } from './RouletteWorld3D'
import { Sound } from './Sound'
import { SettingsState } from './SettingsState'

import { createBoardForBets } from '../utilities/boardCreator'
import { getGameChips } from '../utilities/gameChipsGetter'


export class MainGameState {
    private static readonly DEFAULT_START_BALANCE: number = 5000
    public static readonly COEFFICIENTS: Record<SpotValueType, number> = Object.freeze({
        [SpotValueType.EXACT_NUMBER]: 36,
        [SpotValueType.RED_ONLY]: 2,
        [SpotValueType.BLACK_ONLY]: 2,
        [SpotValueType.EVEN_ONLY]: 2,
        [SpotValueType.ODD_ONLY]: 2,
        [SpotValueType.FIRST_HALF]: 2,
        [SpotValueType.SECOND_HALF]: 2,
        [SpotValueType.FIRST_TWELVE]: 3,
        [SpotValueType.SECOND_TWELVE]: 3,
        [SpotValueType.THIRD_TWELVE]: 3,
        [SpotValueType.FIRST_2_TO_1]: 3,
        [SpotValueType.SECOND_2_TO_1]: 3,
        [SpotValueType.THIRD_2_TO_1]: 3,
    })

    private readonly spotsOnRoulette: Array<RouletteSpot>

    public board: Board
    public currentStage: BaseGameState
    public userBalance: number
    public totalCurrentBet: number
    public chipActiveIndex: number
    public chipsSet: Array<Chip>
    public currentlyHighlightedCells: HighlightedCellsOnHover | null
    public resultsHistory: Array<ResultsHistoryItem>
    public wayTo3DWorld: RouletteWorld3D | null
    public settingsState: SettingsState


    public constructor(startBalance: number = MainGameState.DEFAULT_START_BALANCE) {
        this.userBalance = startBalance
        this.currentStage = BaseGameState.BETS_PLACING
        this.totalCurrentBet = 0
        this.board = createBoardForBets()
        this.chipsSet = getGameChips()
        this.chipActiveIndex = 1
        this.chipsSet[this.chipActiveIndex].active = true
        this.currentlyHighlightedCells = null
        this.wayTo3DWorld = null

        this.settingsState = {
            voiceTurnedOn: true,
            modalsState: {
                modalWarningActive: false,
                modalResultActive: false,
                modalWarningText: '',
                modalInformationActive: false,
                modalPreviousResultActive: false,
                modalPreviousResultData: null,
            }, toHighlightLastResult: false,
            toShowCoefficientsTable: false,
            toShowResultsHistory: false,
            currentTime: new Date(),
            loading: true,
            showLoadingScreen: true
        }
        this.startUpdatingTime()
        makeAutoObservable(this, {}, { deep: true })

        this.spotsOnRoulette = this.formRouletteSpotsArray()
        this.resultsHistory = Array<ResultsHistoryItem>()
    }

    public reselectChip(newIndex: number): void {
        if (this.chipActiveIndex === newIndex)
            return

        this.chipsSet[this.chipActiveIndex].active = false
        this.chipActiveIndex = newIndex
        this.chipsSet[this.chipActiveIndex].active = true
    }

    public putChipOnCell(cell: Spot): void {
        if (this.userBalance < this.chipsSet[this.chipActiveIndex].chip)
            return

        cell.chipsPlaced.push(this.chipsSet[this.chipActiveIndex])
        this.userBalance -= this.chipsSet[this.chipActiveIndex].chip
        this.totalCurrentBet += this.chipsSet[this.chipActiveIndex].chip
        cell.totalBet += this.chipsSet[this.chipActiveIndex].chip
    }

    public cancelBets(): void {
        this.userBalance += this.totalCurrentBet
        this.totalCurrentBet = 0
        this.clearBoardFromBets()
    }

    private formRouletteSpotsArray(): Array<RouletteSpot> {
        const response: Array<RouletteSpot> = Array<RouletteSpot>(37)
        this.board.spots.forEach((spot: Spot) => {
            if (spot.type === SpotValueType.EXACT_NUMBER)
                response[spot.value as number] = { color: spot.color, value: spot.value as number }
        })

        return response
    }

    private checkIfBetWon(spot: Spot, rouletteResult: number): boolean {
        switch (spot.type) {
            case SpotValueType.EVEN_ONLY:
                return this.spotsOnRoulette[rouletteResult].value % 2 === 0
            case SpotValueType.ODD_ONLY:
                return this.spotsOnRoulette[rouletteResult].value % 2 === 1
            case SpotValueType.EXACT_NUMBER:
                return spot.value === this.spotsOnRoulette[rouletteResult].value
            case SpotValueType.BLACK_ONLY:
                return this.spotsOnRoulette[rouletteResult].color === SpotColor.BLACK
            case SpotValueType.RED_ONLY:
                return this.spotsOnRoulette[rouletteResult].color === SpotColor.RED
            case SpotValueType.FIRST_2_TO_1:
                return this.spotsOnRoulette[rouletteResult].value % 3 === 0
            case SpotValueType.SECOND_2_TO_1:
                return (this.spotsOnRoulette[rouletteResult].value - 2) % 3 === 0
            case SpotValueType.THIRD_2_TO_1:
                return (this.spotsOnRoulette[rouletteResult].value - 1) % 3 === 0
            case SpotValueType.FIRST_TWELVE:
                return this.spotsOnRoulette[rouletteResult].value >= 1
                    && this.spotsOnRoulette[rouletteResult].value <= 12
            case SpotValueType.SECOND_TWELVE:
                return this.spotsOnRoulette[rouletteResult].value >= 13
                    && this.spotsOnRoulette[rouletteResult].value <= 24
            case SpotValueType.THIRD_TWELVE:
                return this.spotsOnRoulette[rouletteResult].value >= 25
                    && this.spotsOnRoulette[rouletteResult].value <= 36
            case SpotValueType.FIRST_HALF:
                return this.spotsOnRoulette[rouletteResult].value >= 1 &&
                    this.spotsOnRoulette[rouletteResult].value <= 18
            case SpotValueType.SECOND_HALF:
                return this.spotsOnRoulette[rouletteResult].value >= 19 &&
                    this.spotsOnRoulette[rouletteResult].value <= 36
        }

        return false
    }

    public countResults(rouletteResult: number): void {
        Sound.playResultAnnouncement(rouletteResult)

        window.setTimeout(() => {
            let totalWin: number = 0
            const onlySpotsWithBets: Array<Spot> = this.board.spots.filter(
                (spot: Spot) => spot.chipsPlaced.length !== 0 && spot.totalBet !== 0)

            onlySpotsWithBets.forEach((spot: Spot) => {
                if (this.checkIfBetWon(spot, rouletteResult))
                    totalWin += spot.totalBet * MainGameState.COEFFICIENTS[spot.type]
            })


            totalWin > 0 && Sound.playWin()
            totalWin === 0 && Sound.playLose()


            this.resultsHistory.push({
                award: totalWin,
                result: this.spotsOnRoulette[rouletteResult],
                time: new Date(),
                totalUserBet: this.totalCurrentBet
            })

            this.userBalance += totalWin

            this.settingsState.modalsState.modalResultActive = true


            this.settingsState.toHighlightLastResult = true
            window.setTimeout(() => {
                this.currentStage = BaseGameState.BETS_PLACING
                Sound.playChooseChip()
                this.totalCurrentBet = 0
                this.clearBoardFromBets()
                this.settingsState.toHighlightLastResult = false
            }, 4000)
            window.setTimeout(() => {
                this.settingsState.modalsState.modalResultActive = false
                this.wayTo3DWorld!.moveTheCameraAway()
                this.wayTo3DWorld!.startDefaultAnimations()
            }, 3000)
        }, 2000)
    }


    private clearBoardFromBets(): void {
        this.board.spots.forEach((spot: Spot) => {
            spot.chipsPlaced.length = 0
            spot.totalBet = 0
        })
    }

    public spinRoulette(): void {
        if (this.currentStage === BaseGameState.ROULETTE_SPINNING
            || this.totalCurrentBet === 0)
            return

        this.currentStage = BaseGameState.ROULETTE_SPINNING

        this.wayTo3DWorld!.startDisperse()
    }

    public changeSoundState(): void {
        this.settingsState.voiceTurnedOn = !this.settingsState.voiceTurnedOn
        this.settingsState.voiceTurnedOn ? Sound.backgroundMusicOn() : Sound.backgroundMusicOff()
    }

    private startUpdatingTime(): void {
        this.updateTimeRecursive()
    }

    private updateTimeRecursive(): void {
        window.setTimeout(() => {
            this.settingsState.currentTime = new Date()
            this.updateTimeRecursive()
        }, 3000)
    }
}